import React, { memo, useEffect } from '../../lib/teact/teact';
import { getActions } from '../../global';

import type { ApiNft } from '../../api/types';

import { parseTxId } from '../../util/activities';
import buildClassName from '../../util/buildClassName';
import captureKeyboardListeners from '../../util/captureKeyboardListeners';
import { copyTextToClipboard } from '../../util/clipboard';
import { ANIMATED_STICKERS_PATHS } from '../ui/helpers/animatedAssets';

import useHistoryBack from '../../hooks/useHistoryBack';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import TransferResult from '../common/TransferResult';
import AnimatedIconWithPreview from '../ui/AnimatedIconWithPreview';
import Button from '../ui/Button';
import ModalHeader from '../ui/ModalHeader';
import NftChips from './NftChips';
import NftInfo from './NftInfo';

import styles from '../common/TransferResult.module.scss';
import modalStyles from '../ui/Modal.module.scss';

interface OwnProps {
  isActive: boolean;
  isPending?: boolean;
  isBroadcasted?: boolean;
  isPreparing?: boolean;
  hideRepeat?: boolean;
  amount?: bigint;
  symbol: string;
  txId?: string;
  tokenSlug: string;
  toAddress?: string;
  comment?: string;
  decimals?: number;
  nfts?: ApiNft[];
  isNftBurn?: boolean;
  onInfoClick: NoneToVoidFunction;
  onClose: NoneToVoidFunction;
}

function TransferComplete({
  isActive,
  isPending,
  isBroadcasted,
  isPreparing,
  hideRepeat,
  amount,
  symbol,
  txId,
  tokenSlug,
  toAddress,
  comment,
  decimals,
  nfts,
  isNftBurn,
  onInfoClick,
  onClose,
}: OwnProps) {
  const { startTransfer, showToast, showError } = getActions();

  const lang = useLang();
  const isNftTransfer = Boolean(nfts?.length);
  const transactionHash = txId ? parseTxId(txId).hash : undefined;
  const title = isNftBurn && nfts?.length
    ? lang('$nfts_burned', nfts.length) as string
    : isPending
      ? lang(isPreparing ? '$wallet_transfer_preparing_title'
        : isBroadcasted ? '$wallet_transfer_broadcasted_title' : '$wallet_transfer_pending_title')
      : lang('Sent');

  useHistoryBack({
    isActive,
    onBack: onClose,
  });

  useEffect(() => {
    return isActive
      ? captureKeyboardListeners({ onEnter: onClose })
      : undefined;
  }, [isActive, onClose]);

  const handleTransactionRepeatClick = useLastCallback(() => {
    startTransfer({
      tokenSlug,
      toAddress,
      amount,
      comment,
    });
  });

  const handleCopyTransactionId = useLastCallback(() => {
    if (!transactionHash) return;
    void copyTextToClipboard(transactionHash)
      .then(() => showToast({ message: lang('Transaction ID Copied'), icon: 'icon-copy' }))
      .catch(() => showError({ error: lang('$wallet_transaction_id_copy_failed') }));
  });

  return (
    <>
      <ModalHeader title={title} onClose={onClose} />

      <div className={buildClassName(modalStyles.transitionContent, isPending && styles.pendingContent)}>
        {isNftTransfer ? (
          <>
            <AnimatedIconWithPreview
              play={isActive}
              noLoop={false}
              nonInteractive
              className={styles.sticker}
              tgsUrl={ANIMATED_STICKERS_PATHS.thumbUp}
              previewUrl={ANIMATED_STICKERS_PATHS.thumbUpPreview}
            />
            {nfts.length === 1 ? <NftInfo nft={nfts[0]} /> : <NftChips nfts={nfts} />}
            {Boolean(txId) && (
              <div className={buildClassName(styles.buttons, styles.buttonsAfterNft)}>
                <Button className={styles.button} onClick={onInfoClick}>{lang('Details')}</Button>
              </div>
            )}
          </>
        ) : (
          <TransferResult
            playAnimation={isActive}
            isPending={isPending}
            amount={amount ? -amount : undefined}
            tokenSymbol={symbol}
            firstButtonText={!isPending && txId ? lang('Details') : undefined}
            secondButtonText={isPending || hideRepeat ? undefined : lang('Repeat')}
            onFirstButtonClick={onInfoClick}
            onSecondButtonClick={handleTransactionRepeatClick}
            decimals={decimals}
          />
        )}

        {isPending ? (
          <>
            {isBroadcasted && (
              <div className={styles.pendingBroadcasted} role="status">
                <i className="icon-check" aria-hidden />
                {lang('$wallet_transfer_broadcasted_status')}
              </div>
            )}
            <div className={styles.pendingMessage}>
              {lang(isPreparing ? '$wallet_transfer_preparing_body'
                : isBroadcasted ? '$wallet_transfer_broadcasted_body' : '$wallet_transfer_pending_body')}
            </div>
            {transactionHash && (
              <div className={styles.pendingHash}>
                <span className={styles.pendingHashLabel}>{lang('Transaction ID')}</span>
                <div className={styles.pendingHashValue}>
                  <code>{transactionHash}</code>
                  <span className={styles.pendingCopyButtonWrap} title={lang('Copy Transaction ID')}>
                    <Button
                      isSimple
                      className={styles.pendingCopyButton}
                      ariaLabel={lang('Copy Transaction ID')}
                      onClick={handleCopyTransactionId}
                    >
                      <i className="icon-copy-bold" aria-hidden />
                    </Button>
                  </span>
                </div>
              </div>
            )}
            <div className={styles.pendingActions}>
              {txId && !isPreparing && (
                <Button isPrimary onClick={onInfoClick}>{lang('View details')}</Button>
              )}
              <Button onClick={onClose}>{lang('Close')}</Button>
            </div>
          </>
        ) : (
          <div className={modalStyles.buttons}>
            <Button onClick={onClose} isPrimary>{lang('Close')}</Button>
          </div>
        )}
      </div>
    </>
  );
}

export default memo(TransferComplete);
